import {
  ErrorHandler,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { environment } from './environments/environment';
import {
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import { tokenInterceptor } from './app/core/interceptors/token.interceptor';
import { networkRetryInterceptor } from './app/core/interceptors/network-retry.interceptor';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  PreloadAllModules,
  provideRouter,
  withPreloading,
} from '@angular/router';
import { routes } from './app/app.routes';
import { provideTranslateService } from '@ngx-translate/core';
import { ApiModule } from './api';
import { provideServiceWorker } from '@angular/service-worker';
import { AppComponent } from './app/app.component';
import { provideAppMaterial } from './app/shared/material/material.providers';
import { LoggerErrorHandler } from './app/logger-error.handler';
import { httpLoggingInterceptor } from './app/core/interceptors/http-logging.interceptor';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { RecordersStore } from './app/core/store/recorders.store';
import { AuthService, provideAuth0 } from '@auth0/auth0-angular';
import { Stage } from './app/shared/enums/stage.enum';
import { UserStore } from './app/core/store/user.store';
import { ClubsStore } from './app/core/store/clubs.store';
import { TeamsStore } from './app/core/store/teams.store';
import { TrashStore } from './app/core/store/trash.store';
import { CalendarStore } from './app/core/store/calendar.store';
import { SharingStore } from './app/core/store/sharing.store';
import { FilterSearchStore } from './app/core/store/filter-search.store';
import { VideoStore } from './app/core/store/video.store';
import { PtzStore } from './app/core/store/ptz.store';
import { UiStore } from './app/core/store/ui.store';
import { RouteStore } from './app/core/store/route.store';
import { RecordingStore } from './app/core/store/recording.store';
import { RecordingListStore } from './app/core/store/recording-list.store';
import { AnnotationsStore } from './app/core/store/annotations.store';
import { AnnotationTypesStore } from './app/core/store/annotation-types.store';
import { AnnotationListsStore } from './app/core/store/annotation-lists.store';
import { PlaylistsStore } from './app/core/store/playlists.store';
import { CurrentSelectionsStore } from './app/core/store/current-selections.store';
import { WsStore } from './app/core/store/ws.store';
import { AppInitService } from './app/core/services/app-init.service';
import { RouteEffectsService } from './app/core/services/route-effects.service';
import { WsService } from './app/core/services/ws.service';
import { DEFAULT_LANG } from './app/app.constants';
import { MockAuthService } from './app/core/auth/mock-auth.service';

if (environment.stage === Stage.Production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    ...provideAppMaterial(),
    importProvidersFrom(ApiModule.forRoot({ rootUrl: environment.apiUrl })),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.stage === Stage.Production,
      registrationStrategy: 'registerImmediately',
    }),
    RecordersStore,
    UserStore,
    ClubsStore,
    TeamsStore,
    TrashStore,
    CalendarStore,
    SharingStore,
    FilterSearchStore,
    VideoStore,
    PtzStore,
    UiStore,
    RouteStore,
    RecordingStore,
    RecordingListStore,
    AnnotationsStore,
    AnnotationTypesStore,
    AnnotationListsStore,
    PlaylistsStore,
    CurrentSelectionsStore,
    WsStore,
    /* Start the bootstrap orchestration: the router read-model, the effects a
     * navigation triggers, the WebSocket hub, and the after-login initial loads
     * (formerly the eagerly-instantiated route/ws/init effect classes). */
    provideAppInitializer(() => {
      inject(RouteStore);
      inject(RouteEffectsService);
      inject(WsService);
      inject(AppInitService);
    }),
    // Feature flags are per-user and the endpoint is authenticated, so they are
    // loaded after login (see app-init.service.ts) rather than at app startup.
    ...(environment.useMockAuth
      ? [
          {
            provide: AuthService,
            useClass: MockAuthService as unknown as typeof AuthService,
          },
        ]
      : [
          provideAuth0({
            domain: environment.auth0.domain,
            clientId: environment.auth0.clientId,
            authorizationParams: {
              audience: environment.auth0.audience,
              redirect_uri: environment.auth0.redirectUri,
              scope: environment.auth0.scope,
              api_url: environment.apiUrl,
              api_env: environment.stage,
            },
            useRefreshTokens: true,
            cacheLocation: 'localstorage',
          }),
        ]),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/languages/',
        suffix: '.json',
      }),
      fallbackLang: DEFAULT_LANG,
      lang: DEFAULT_LANG,
    }),
    ...(environment.logger.enabled
      ? [
          {
            provide: ErrorHandler,
            useClass: LoggerErrorHandler,
          },
        ]
      : []),
    provideHttpClient(
      withXhr(),
      withInterceptors([
        networkRetryInterceptor,
        tokenInterceptor,
        ...(environment.logger.enabled ? [httpLoggingInterceptor] : []),
      ]),
    ),
  ],
}).catch(err => console.error(err));
